import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import {
  SortableContext,
  horizontalListSortingStrategy,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Box, Modal, Typography, TextField, Button } from '@mui/material'
import { reorder } from './util'

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4
}

function SortableItem ({ id, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id })

  const itemStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.85 : undefined,
    zIndex: isDragging ? 2 : undefined,
    touchAction: 'none'
  }

  return (
    <div ref={setNodeRef} style={itemStyle} {...attributes} {...listeners}>
      {children}
    </div>
  )
}

function sortingStrategy (direction, maxItems) {
  if (direction === 'vertical') return verticalListSortingStrategy
  if (maxItems) return rectSortingStrategy
  return horizontalListSortingStrategy
}

const DraggableContainer = ({ items, direction, maxItems, component }) => {
  const [listItems, setItems] = useState(items)
  const [modalProp, setModalProp] = useState(false)
  const [newOrder, setOrder] = useState('')
  const dispatch = useDispatch()

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  )

  useEffect(() => {
    setItems(items)
  }, [items])

  function commitReorder (sourceIndex, destinationIndex) {
    if (sourceIndex === destinationIndex) return

    const draggedItem = listItems[sourceIndex]
    const reorderedItems = reorder(
      listItems,
      sourceIndex,
      destinationIndex
    )

    setItems(reorderedItems)

    dispatch(draggedItem.moveAction({
      draggedIndex: sourceIndex,
      droppedIndex: destinationIndex
    }))
  }

  function handleDragEnd (event) {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const sourceIndex = listItems.findIndex((i) => i.id === active.id)
    const destinationIndex = listItems.findIndex((i) => i.id === over.id)
    if (sourceIndex === -1 || destinationIndex === -1) return

    commitReorder(sourceIndex, destinationIndex)
  }

  const Component = component
  const sortableIds = listItems.map((i) => i.id)
  const strategy = sortingStrategy(direction, maxItems)

  function onSubmit () {
    commitReorder(modalProp.order, Number(newOrder))
    setOrder('')
    setModalProp('')
  }

  return (
    <Box>
      {modalProp && (
        <Modal open={Boolean(modalProp)} onClose={() => setModalProp(false)}>
          <Box sx={style}>
            <Typography id='modal-modal-title' variant='h6' component='h2'>
              Moving Lesson: {modalProp.title}
            </Typography>
            <Box
              component='form'
              sx={{
                '& > :not(style)': { m: 1, width: '25ch' },
                display: 'flex'
              }}
              noValidate
              autoComplete='off'
            >
              <TextField id='standard-basic' label='Current Order' variant='standard' disabled value={modalProp.order} />
              <TextField id='standard-basic' autoFocus label='New Order' variant='standard' placeholder='number' value={newOrder} onChange={(e) => setOrder(e.target.value)} />
            </Box>
            <Box>
              <Button variant='contained' disabled={newOrder === ''} onClick={onSubmit}>Set Order</Button>
            </Box>
          </Box>
        </Modal>
      )}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sortableIds} strategy={strategy}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: direction === 'vertical' ? 'column' : 'row',
              flexWrap: direction === 'horizontal' ? 'wrap' : 'nowrap',
              alignContent: 'flex-start'
            }}
          >
            {listItems.map((item) => (
              <SortableItem key={item.id} id={item.id}>
                <Component {...item.props} onDragEnd={commitReorder} setModalProp={setModalProp} newOrder={newOrder} />
              </SortableItem>
            ))}
          </Box>
        </SortableContext>
      </DndContext>
    </Box>
  )
}

export default DraggableContainer
